import { Reveal } from "@components/ui";
import warningIllustration from "@assets/images/safety-warning-illustration.svg";

const safetyTips = [
  {
    text: "Ensure proper ventilation in the kitchen",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5">
        <path d="M8 2h8l4 10H4L8 2z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M12 12v4M8 22h8M10 18h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M7 16h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    text: "Keep stoves and burners clean",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5">
        <path d="M12 2C8 6 4 9.58 4 14a8 8 0 0016 0c0-4.42-4-8-8-12z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    text: "Check flame color (blue = good, yellow = incomplete combustion)",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5">
        <circle cx="12" cy="12" r="5" stroke="currentColor" strokeWidth="1.5" />
        <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    text: "Never block or hide the gas meter",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
        <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    text: "Avoid placing items close to gas burners",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5">
        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M12 9v4M12 17h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    text: "Turn off gas when leaving home for long periods",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5">
        <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" strokeWidth="1.5" />
        <path d="M7 11V7a5 5 0 0110 0v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="12" cy="16" r="1" fill="currentColor" />
      </svg>
    ),
  },
  {
    text: "Call 800-GASIS immediately if you smell gas",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5">
        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
];

export function SafetyTipsSection() {
  return (
    <section className="w-full bg-[#f1f1f1] overflow-hidden py-12 md:py-16 lg:py-[100px] px-5 md:px-10 lg:px-[180px]">
      <div className="w-full max-w-[1368px] mx-auto">
        <div className="flex flex-col lg:flex-row items-start gap-8 md:gap-12 lg:gap-20">
          {/* Left Content */}
          <Reveal direction="left" className="flex-1 min-w-0">
            <div className="flex flex-col items-start gap-3">
              <span className="inline-block px-4 py-1.5 rounded-full bg-[#122d63]/8 text-[#122d63] text-[13px] md:text-[15px] font-semibold tracking-[-0.02em] uppercase">
                Safety
              </span>
              <h2 className="text-[24px] md:text-[40px] lg:text-[60px] tracking-[-0.04em] font-semibold text-[#122d63] font-albert-sans leading-[1.1]">
                Residential Safety Tips
              </h2>
              <p className="text-[14px] md:text-base lg:text-[24px] tracking-[-0.04em] leading-[34px] text-[#62636c] font-albert-sans">
                Keep your home and family safe with these important guidelines
              </p>

              {/* Numbered List */}
              <div className="self-stretch flex flex-col pt-4 md:pt-[23px]">
                {safetyTips.map((tip, index) => (
                  <Reveal key={tip.text} direction="up" delay={index * 60} className="self-stretch">
                    <div className="border-b border-[rgba(98,99,108,0.25)] flex items-center gap-3 md:gap-5 pt-4 md:pt-[28px] px-2 pb-3 md:pb-[19px] group cursor-pointer hover:bg-white/60 transition-all duration-300 rounded-[8px] hover:px-4">
                      {/* Tinted Number Container */}
                      <div className="w-8 h-8 md:w-10 md:h-10 rounded-[10px] bg-[#122d63]/8 text-[#122d63] flex items-center justify-center shrink-0 group-hover:bg-[#122d63] group-hover:text-white transition-colors duration-300">
                        <span className="text-[14px] md:text-[16px] font-semibold font-albert-sans">
                          {String(index + 1).padStart(2, "0")}
                        </span>
                      </div>
                      {/* Icon */}
                      <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-[#f03813]/10 text-[#f03813] flex items-center justify-center shrink-0">
                        {tip.icon}
                      </div>
                      {/* Text */}
                      <p className="text-[15px] md:text-[20px] lg:text-[24px] tracking-[-0.04em] font-medium text-black font-albert-sans group-hover:text-[#122d63] transition-colors">
                        {tip.text}
                      </p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </Reveal>

          {/* Right — Warning Illustration */}
          <Reveal direction="right" className="w-full lg:w-auto lg:self-stretch lg:max-w-[615px] shrink-0">
            <img
              src={warningIllustration}
              alt="Gas safety warning"
              className="w-full h-full object-cover rounded-[24px]"
            />
          </Reveal>
        </div>
      </div>
    </section>
  );
}
